/**
 * @fileoverview `useCaptchaToken()` — the token lifecycle a surface that gates a submit on
 *   `<CaptchaField>` needs, in one hook.
 * @module @puffin-core/captcha-ui/useCaptchaToken
 *
 * CAPTCHA-POLICY-001 (SCALE-ACTIVATE-001 S3-21, 2026-09-08). A surface whose API route
 * verifies the token fail-closed (pos-api's `/temporary-session`, `/verify-credentials`) needs
 * the same four things every time: the token the widget issued, the widget's imperative ref,
 * the three widget callbacks, and a way to start over after a failed submit. Before this hook
 * each pos-web surface hand-rolled them (`useRef` + `useState` + two `useCallback`s), which
 * is how one of them shipped a widget wired to nothing.
 *
 * Two distinct "clear" operations, on purpose:
 *
 *   - `clear()` — forget the token. Used when the WIDGET reports expiry or error: it has
 *     already reset itself, so touching it again would double-reset.
 *   - `reset()` — forget the token AND reset the widget. Used after a SUBMIT fails: a Turnstile
 *     token is single-use (one siteverify call consumes it), so the retry needs a fresh solve
 *     or the server answers `timeout-or-duplicate`.
 *
 * `fieldProps` spreads straight onto the component: `<CaptchaField {...captcha.fieldProps} />`.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * @typedef {Object} CaptchaTokenHandle
 * @property {string|null} token - The token the widget issued, or `null` while blocking.
 * @property {{current: (Object|null)}} ref - The `<CaptchaField>` imperative ref.
 * @property {{ref: {current: (Object|null)}, onVerify: (token: string) => void, onExpire: () => void,
 *   onError: () => void}} fieldProps
 *   Stable props for `<CaptchaField>` — spread them onto the element.
 * @property {() => void} clear - Forget the token (widget expiry / error).
 * @property {() => void} reset - Forget the token AND reset the widget (a failed submit).
 */

/**
 * The CAPTCHA token lifecycle for one `<CaptchaField>`.
 *
 * @returns {CaptchaTokenHandle} A handle whose identity changes only when the token does.
 */
export function useCaptchaToken() {
  const ref = useRef(null);
  const [token, setToken] = useState(null);

  const clear = useCallback(() => {
    setToken(null);
  }, []);

  const reset = useCallback(() => {
    setToken(null);
    ref.current?.reset?.();
  }, []);

  return useMemo(
    () => ({
      token,
      ref,
      fieldProps: { ref, onVerify: setToken, onExpire: clear, onError: clear },
      clear,
      reset,
    }),
    [token, clear, reset],
  );
}

export default useCaptchaToken;
